import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const TEAL   = '#14b8a6';
const GREEN  = '#34d399';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const CARD   = '#1e2329';
const SUNK   = '#171b21';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1250, fontFamily: 'system-ui', padding: 20 },
  card:    { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px 15px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 },
  title:   { fontSize: 15, fontWeight: 700, color: TEXT, display: 'flex', alignItems: 'center', gap: 9 },
  body:    { padding: '20px 22px 24px', overflowY: 'auto', flex: 1 },
  footer:  { padding: '13px 22px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, flexShrink: 0 },
  btnPrim: { padding: '9px 20px', background: TEAL, color: '#04201d', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  btnGhost:{ padding: '9px 16px', background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  eyebrow: { fontSize: 10, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em' },
};

const nf = n => (Number(n) || 0).toLocaleString();
function fmtShort(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Applying a capacity change to work already on the board.
//
// Nothing here writes until Apply. The preview and the apply run the same
// calculation, and the apply works it out again server-side, so what lands is
// what the board supports at that moment rather than what this screen last saw.
export default function ReflowModal({ onClose, machineIds = [], machineNames = [], from = null, onApplied, onUndone }) {
  const [pullForward, setPullForward] = useState(true);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError]     = useState(null);
  const [done, setDone]       = useState(null);
  const [minimised, setMinimised] = useState(false);
  // The director's choices so far (§7, §8): holds, machines brought in to make
  // room, clashes kept to settle by hand. Sent with every preview and with the
  // apply, which works it all out again.
  const [constraints, setConstraints] = useState({ holds: [], in_place: [], accepted: [], step_over: [] });
  const [lastChoice, setLastChoice] = useState(null);

  const load = useCallback(async (pf) => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.post('/api/v1/reflow/preview', {
        machine_ids: machineIds, pull_forward: pf,
        // Given only for a backdated window, where laying the runs from today
        // would leave the very days that ran extra shifts untouched.
        ...(from ? { from } : {}),
        ...constraints,
      });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not work out the changes');
    } finally {
      setLoading(false);
    }
  }, [machineIds, from, constraints]);

  useEffect(() => { load(pullForward); }, [load, pullForward]);

  async function apply() {
    setApplying(true); setError(null);
    try {
      const { data: d } = await api.post('/api/v1/reflow/apply', {
        machine_ids: machineIds,
        pull_forward: pullForward,
        ...(from ? { from } : {}),
        ...constraints,
        reason: `Re-flow · ${machineNames.join(', ')}`,
      });
      setDone(d);
      setMinimised(true);
      onApplied && onApplied(d?.snapshot_id);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not apply the changes');
    } finally {
      setApplying(false);
    }
  }

  async function undo() {
    if (!done?.snapshot_id) return;
    setApplying(true); setError(null);
    try {
      await api.post(`/api/v1/reflow/undo/${done.snapshot_id}`);
      setDone(null);
      setMinimised(false);
      onUndone && onUndone();
      await load(pullForward);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not undo');
    } finally {
      setApplying(false);
    }
  }

  const s = data?.summary;
  const clashes = data?.clashes || { count: 0 };
  const unsettled = (clashes.count || 0) > 0 || !!clashes.error;
  function choose(opt) {
    if (!opt || !opt.ok || !opt.constraints) return;
    setLastChoice(opt.says);
    setConstraints(opt.constraints);
  }
  function startAgain() {
    setLastChoice('you started the choices again');
    setConstraints({ holds: [], in_place: [], accepted: [], step_over: [] });
  }
  // A pin (§8): hold a run until a date, or where it is; a reason to remember.
  function addHold(run, until, note) {
    setLastChoice(`hold ${run.part}${until ? ` until ${fmtShort(until)}` : ' where it is'}`);
    setConstraints(c => ({
      ...c,
      holds: [...c.holds.filter(h => h.plan_id !== run.plan_id),
        { plan_id: run.plan_id, until: until || null, why: 'pin', says: `${run.part} held${note ? ` (${note})` : ''}`, note: note || null }],
      step_over: (c.step_over || []).filter(x => x.plan_id !== run.plan_id),
    }));
  }
  function releaseHold(planId, part) {
    setLastChoice(`release ${part || 'the hold'}`);
    setConstraints(c => ({
      ...c,
      holds: c.holds.filter(h => h.plan_id !== planId),
      step_over: (c.step_over || []).filter(x => x.plan_id !== planId),
    }));
  }
  const movers = (data?.machines || [])
    .map(m => ({ ...m, runs: (m.runs || []).filter(r => r.moved || r.frozen) }))
    .filter(m => m.runs.length > 0);

  if (done && minimised) {
    return <Pill done={done} onExpand={() => setMinimised(false)}
                 onUndo={undo} onClose={onClose} busy={applying} error={error} />;
  }

  return (
    <div style={st.overlay} onClick={done ? undefined : onClose}>
      <div style={st.card} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <div style={st.title}>
            <i className="fa-solid fa-arrows-left-right-to-line" style={{ color: TEAL }} />
            Re-flow the schedule
          </div>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 16, cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>

        <div style={st.body}>
          {done ? (
            <Applied done={done} onUndo={undo} busy={applying} movers={movers} summary={s} />
          ) : (
            <>
              <div style={{ fontSize: 13, color: DIM, lineHeight: 1.6, marginBottom: 15 }}>
                Recomputes the dates of work already on
                {' '}<strong style={{ color: TEXT }}>{machineNames.join(', ') || 'these machines'}</strong>{' '}
                using the shifts those days now work. The order never changes — only dates.
              </div>

              <Mode value={pullForward} onChange={setPullForward} />

              {error && (
                <div style={{ marginTop: 15, padding: '12px 14px', background: `${RED}12`,
                              border: `1px solid ${RED}44`, borderRadius: 9, fontSize: 13,
                              color: '#fca5a5', lineHeight: 1.55 }}>
                  {error}
                </div>
              )}

              {loading ? (
                <div style={{ marginTop: 18, color: MUTED, fontSize: 13 }}>Working it out…</div>
              ) : s ? (
                <>
                  {(lastChoice || constraints.holds.length > 0 || constraints.accepted.length > 0) && (
                    <Choices constraints={constraints} lastChoice={lastChoice} onStartAgain={startAgain}
                      pins={data.pins || []} pinsError={data.pins_error} onRelease={releaseHold} onChoose={choose} />
                  )}
                  {clashes.error && (
                    <div style={{ marginTop: 14, padding: '11px 13px', background: `${RED}12`, border: `1px solid ${RED}44`, borderRadius: 9, fontSize: 12.5, color: '#fca5a5' }}>
                      {clashes.error}
                    </div>
                  )}
                  {clashes.first && <Clash c={clashes.first} more={(clashes.count || 1) - 1} onChoose={choose} />}
                  <Summary s={s} frozenUntil={data.frozen_until}
                           windowsApply={data.windows_apply} pullForward={pullForward} />
                  {/* A window is a spending decision: say when its extra shifts buy nothing (§8). */}
                  {(data.window_use || []).map(w => (
                    <div key={w.group_id} style={{ marginTop: 10, padding: '10px 12px', background: `${AMBER}0f`, border: `1px solid ${AMBER}40`, borderRadius: 9, fontSize: 12.5, color: '#fcd34d', lineHeight: 1.5 }}>
                      <i className="fa-solid fa-coins" style={{ marginRight: 7 }} />{w.says}
                    </div>
                  ))}
                  {movers.length === 0 ? (
                    <div style={{ marginTop: 16, padding: '16px', background: SUNK,
                                  border: `1px dashed ${BORDER}`, borderRadius: 10,
                                  fontSize: 13, color: MUTED, textAlign: 'center' }}>
                      Nothing moves. The board already matches the capacity it has.
                    </div>
                  ) : movers.map(m => (
                    <MachineBlock key={m.machine_id} m={m} onHold={addHold} />
                  ))}
                  <People groups={data.people} error={data.people_error} onChanged={() => load(pullForward)} />
                </>
              ) : null}
            </>
          )}
        </div>

        <div style={st.footer}>
          {done && (
            <button style={st.btnGhost} onClick={() => setMinimised(true)}>
              Shrink to corner
            </button>
          )}
          {/* Says what it does and, by omission, what it does not. Cancelling
              here leaves the dates alone; it does not undo a window that was
              declared before this screen opened, which is a separate decision
              with its own Remove. */}
          <button style={st.btnGhost} onClick={onClose}
            title={done ? undefined : 'Nothing is written — the dates stay as they are'}>
            {done ? 'Done' : 'Cancel — leave the dates alone'}
          </button>
          {!done && (
            <button onClick={apply}
              title={unsettled ? 'Settle the mould clash above first' : undefined}
              disabled={loading || applying || !s || s.runs_moved === 0 || unsettled}
              style={{ ...st.btnPrim,
                       opacity: (loading || applying || !s || s.runs_moved === 0 || unsettled) ? 0.4 : 1,
                       cursor: (loading || applying || !s || s.runs_moved === 0 || unsettled) ? 'not-allowed' : 'pointer' }}>
              {applying ? 'Applying…' : `Apply${s?.runs_moved ? ` — move ${s.runs_moved} run${s.runs_moved === 1 ? '' : 's'}` : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// The gap a compressed run leaves behind: close it, or leave it.
function Mode({ value, onChange }) {
  const opt = (on, label, note) => (
    <button onClick={() => onChange(on)}
      style={{ flex: 1, textAlign: 'left', padding: '11px 13px', borderRadius: 9, cursor: 'pointer',
               background: value === on ? `${TEAL}14` : SUNK,
               border: `1px solid ${value === on ? TEAL : BORDER}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: value === on ? TEAL : TEXT }}>{label}</div>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 3, lineHeight: 1.45 }}>{note}</div>
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 9 }}>
      {opt(true,  'Pull work forward', 'Later runs close the gap, so everything finishes sooner.')}
      {opt(false, 'Compress in place', 'Runs shorten where they are. Nothing else moves.')}
    </div>
  );
}

function Summary({ s, frozenUntil, windowsApply, pullForward }) {
  const tone = !s.ok ? RED : s.runs_moved > 0 ? TEAL : MUTED;
  return (
    <div style={{ marginTop: 16, padding: '14px 16px', background: `${tone}10`,
                  border: `1px solid ${tone}40`, borderRadius: 11 }}>
      <div style={{ color: tone, fontWeight: 700, fontSize: 15 }}>
        {s.runs_moved === 0
          ? 'Nothing to move'
          : `${s.runs_moved} run${s.runs_moved === 1 ? '' : 's'} move · ${Math.abs(s.days_saved)} day${Math.abs(s.days_saved) === 1 ? '' : 's'} ${s.days_saved < 0 ? 'added' : 'saved'}`}
      </div>

      {/* "Nothing to move" on its own is the worst kind of answer: it states a
          result, withholds the reason, and offers no way forward. Almost always
          the reason is the frozen zone, and the director can do something about
          it -- but only if he is told. */}
      {s.runs_moved === 0 && (
        <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.6, marginTop: 7 }}>
          {s.runs_frozen > 0 ? (
            <>
              <strong style={{ color: AMBER }}>
                {s.runs_frozen === 1 ? 'The one run here is' : `All ${s.runs_frozen} runs here are`} inside
                the frozen zone
              </strong>
              {frozenUntil ? <>, which runs to {fmtShort(frozenUntil)}</> : null}. Nothing inside it is
              moved automatically &mdash; the floor has set up for those days, and a schedule that
              changes under people loses their trust.
              <div style={{ marginTop: 7, paddingLeft: 2 }}>
                <div>&rarr; Declare the window for dates after {frozenUntil ? fmtShort(frozenUntil) : 'the frozen zone'}, where it can still buy you something</div>
                <div>&rarr; Shorten the frozen zone in settings, if these days really are still yours to change</div>
                <div>&rarr; Drag the runs by hand &mdash; the frozen zone holds back re-flow, not you</div>
              </div>
            </>
          ) : (
            <>These runs already sit as early as this capacity allows, so the window buys nothing on
            work that is already booked. It still applies to anything scheduled from now on.</>
          )}
        </div>
      )}

      {s.runs_moved > 0 && (
        <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.55, marginTop: 6 }}>
          {windowsApply
            ? (s.days_saved > 0
                ? <>Runs shorten because these days now work more shifts{pullForward ? ', and later work closes the gap' : ''}.</>
                : <>A shift window applies here{pullForward ? ', and later work closes the gap it leaves' : ''}.</>)
            : (<>
                <strong style={{ color: AMBER }}>No shift window covers these machines.</strong>{' '}
                Nothing changes length — these runs only move earlier because
                “pull work forward” closes the gaps between them. Choose
                “compress in place” to leave them where they are.
              </>)}
        </div>
      )}

      {s.breaks_deadline.length > 0 && (
        <div style={{ fontSize: 13, color: '#fca5a5', lineHeight: 1.6, marginTop: 7 }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 7 }} />
          {s.breaks_deadline.map(b => (
            <div key={b.plan_id}>
              <strong style={{ color: TEXT }}>{b.delivery_week ? `Wk ${b.delivery_week} ` : ''}{b.part}</strong>
              {' '}would finish {fmtShort(b.new_finish)}, past its {fmtShort(b.deadline)} cut-off.
            </div>
          ))}
        </div>
      )}

      {s.could_not_place.length > 0 && (
        <div style={{ fontSize: 13, color: '#fca5a5', lineHeight: 1.6, marginTop: 7 }}>
          {s.could_not_place.map(c => (
            <div key={c.plan_id}>{c.part}: {nf(c.unplaced)} parts could not be placed.</div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.55 }}>
        {/* Only as a footnote when something else DID move. When nothing moved
            the frozen zone is the whole story, and it is told properly above
            rather than in grey at the bottom -- which is where it used to be,
            under a bare "Nothing to move". */}
        {s.runs_frozen > 0 && s.runs_moved > 0 && (
          <>
            {s.runs_frozen} run{s.runs_frozen === 1 ? '' : 's'} left alone — {frozenUntil
              ? <>they start before {fmtShort(frozenUntil)}, inside the frozen zone.</>
              : <>already under way.</>}
            <br />
          </>
        )}
        Days already worked never move. Every run is checked against its own delivery week.
      </div>
    </div>
  );
}

function MachineBlock({ m, onHold }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...st.eyebrow, marginBottom: 8 }}>{m.machine}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {m.runs.map(r => <RunRow key={r.plan_id} r={r} onHold={onHold} />)}
      </div>
    </div>
  );
}

function RunRow({ r, onHold }) {
  const broken = r.meets_deadline === false;
  // Hold it (§8): usually material that is not there yet. "Until when?" --
  // empty means it does not move at all.
  const [holding, setHolding] = useState(false);
  const [until, setUntil] = useState('');
  const [note, setNote] = useState('');
  const tone = broken ? RED : r.frozen ? MUTED : r.days_saved > 0 ? GREEN : TEAL;
  return (
    <div style={{ padding: '10px 13px', background: SUNK, borderRadius: 9,
                  border: `1px solid ${broken ? `${RED}55` : BORDER}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
          {r.delivery_week ? <span style={{ color: MUTED }}>Wk {r.delivery_week} </span> : null}
          {r.part}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: tone, whiteSpace: 'nowrap' }}>
          {r.frozen ? 'left alone'
            : r.days_saved > 0 ? `${r.days_saved} day${r.days_saved === 1 ? '' : 's'} shorter`
            : r.days_saved < 0 ? `${-r.days_saved} day${r.days_saved === -1 ? '' : 's'} longer`
            : r.new_start < r.current_start ? 'moved earlier'
            : r.new_start > r.current_start ? 'moved later'
            : 'unchanged'}
        </div>
      </div>
      {!r.frozen && (
        <div style={{ fontSize: 12.5, color: DIM, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
          {fmtShort(r.current_start)} – {fmtShort(r.current_finish)}
          <span style={{ color: MUTED }}> → </span>
          <strong style={{ color: TEXT }}>{fmtShort(r.new_start)} – {fmtShort(r.new_finish)}</strong>
          {r.deadline && (
            <span style={{ color: broken ? RED : MUTED }}>
              {' · '}{broken ? 'misses' : 'still makes'} {fmtShort(r.deadline)}
            </span>
          )}
          {onHold && !r.frozen && !holding && (
            <button onClick={() => setHolding(true)} title="Hold this run: until a date, or where it is"
              style={{ marginLeft: 10, background: 'none', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>
              <i className="fa-solid fa-thumbtack" style={{ marginRight: 5, fontSize: 9 }} />Hold
            </button>
          )}
        </div>
      )}
      {holding && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: DIM }}>
          <span>Hold until</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} aria-label="Hold until"
            style={{ padding: '5px 8px', background: '#141920', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 12 }} />
          <span style={{ color: MUTED }}>(empty: where it is)</span>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Why, e.g. material arrives Tuesday" maxLength={200}
            style={{ flex: '1 1 180px', padding: '5px 8px', background: '#141920', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 12 }} />
          <button onClick={() => setHolding(false)} style={{ background: 'none', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => { onHold(r, until || null, note.trim() || null); setHolding(false); }}
            style={{ background: TEAL, border: 'none', color: '#04201d', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Hold</button>
        </div>
      )}
    </div>
  );
}

// Undo stays on screen after applying, because the moment a director wants it
// is the moment they see the result — not after hunting for a history page.
// What just happened, not how many rows it touched.
//
// A count is not a record. The confirmation repeats the list the preview showed,
// now as what actually landed, so a director closing this dialog has seen their
// schedule's new shape rather than been asked to remember it.
function Applied({ done, onUndo, busy, movers = [], summary }) {
  const saved = summary?.days_saved || 0;
  return (
    <>
      <div style={{ padding: '18px 20px', background: `${TEAL}12`, border: `1px solid ${TEAL}40`, borderRadius: 12 }}>
        <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
          <i className="fa-solid fa-circle-check" style={{ color: TEAL, fontSize: 20, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: TEAL, fontWeight: 700, fontSize: 17 }}>Schedule updated</div>
            <div style={{ color: DIM, fontSize: 13, lineHeight: 1.6, marginTop: 5 }}>
              {done.runs} run{done.runs === 1 ? '' : 's'} rewritten across {done.days} day
              {done.days === 1 ? '' : 's'}
              {saved !== 0 && `, ${Math.abs(saved)} day${Math.abs(saved) === 1 ? '' : 's'} ${saved > 0 ? 'saved' : 'added'}`}.
              The board behind this dialog has already changed.
            </div>
            {done.snapshot_id && (
              <button onClick={onUndo} disabled={busy}
                style={{ ...st.btnGhost, marginTop: 13, color: AMBER, borderColor: `${AMBER}55`,
                         opacity: busy ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fa-solid fa-rotate-left" style={{ fontSize: 11 }} />
                {busy ? 'Undoing…' : 'Undo — put it all back'}
              </button>
            )}
            <div style={{ fontSize: 12, color: MUTED, marginTop: 9, lineHeight: 1.5 }}>
              Undo restores the exact dates these runs had a moment ago. It can be used once.
            </div>
          </div>
        </div>
      </div>

      {movers.map(m => {
        const changed = m.runs.filter(r => r.moved);
        if (changed.length === 0) return null;
        return (
          <div key={m.machine_id} style={{ marginTop: 16 }}>
            <div style={{ ...st.eyebrow, marginBottom: 8 }}>{m.machine}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {changed.map(r => <RunRow key={r.plan_id} r={r} />)}
            </div>
          </div>
        );
      })}
    </>
  );
}

// Parked in the corner while the board is checked.
//
// The undo lives on it rather than only inside the dialog, because the minute
// after applying is exactly when it is wanted and the board is exactly what has
// to be visible to decide.
function Pill({ done, onExpand, onUndo, onClose, busy, error }) {
  return (
    <div style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 1250, width: 290,
                  background: CARD, border: `1px solid ${TEAL}55`, borderRadius: 12,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.55)', fontFamily: 'system-ui' }}>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <i className="fa-solid fa-circle-check" style={{ color: TEAL, fontSize: 14 }} />
          <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: TEAL }}>Schedule updated</div>
          <button onClick={onClose} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: MUTED, fontSize: 14, cursor: 'pointer', padding: 0 }}>✕</button>
        </div>

        <div style={{ fontSize: 12.5, color: DIM, marginTop: 5, lineHeight: 1.5 }}>
          {done.runs} run{done.runs === 1 ? '' : 's'} rewritten across {done.days} day
          {done.days === 1 ? '' : 's'}. Check the board.
        </div>

        {error && (
          <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 7, lineHeight: 1.45 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 7, marginTop: 11 }}>
          {done.snapshot_id && (
            <button onClick={onUndo} disabled={busy}
              style={{ flex: 1, padding: '7px 10px', fontSize: 12, fontWeight: 700, borderRadius: 7,
                       background: `${AMBER}18`, color: AMBER, border: `1px solid ${AMBER}55`,
                       cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                       display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <i className="fa-solid fa-rotate-left" style={{ fontSize: 10 }} />
              {busy ? 'Undoing…' : 'Undo'}
            </button>
          )}
          <button onClick={onExpand}
            style={{ flex: 1, padding: '7px 10px', fontSize: 12, borderRadius: 7,
                     background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`, cursor: 'pointer' }}>
            What changed
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the People check (Shift Windows; the director, 9 Oct 2026) ──────────────
//
// Each window's shifts against the work this re-flow would put in them. A
// short shift offers the two ways forward he asked for: a person for those
// days (extra staff that come and go with the window), or "I've got it
// covered" -- how, in his words, kept on the window so a look back months
// later says why the shift ran short.

const SHIFT = { 1: 'morning', 2: 'afternoon', 3: 'night' };
const VERDICT = {
  short:      { word: 'short',            color: RED },
  needs_call: { word: 'needs someone on call', color: AMBER },
  tight:      { word: 'tight',            color: AMBER },
  unknown:    { word: 'cannot tell yet',  color: MUTED },
};
const HOW = [
  ['stay_longer', 'I stay longer'],
  ['do_more_myself', 'I do more of the work myself'],
  ['more_scrap', 'We run faster and accept more scrap'],
  ['overtime', 'Overtime for the team'],
  ['on_call', 'Someone on call comes in'],
];
const dayName = d => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const h = n => { const r = Math.round((Number(n) || 0) * 10) / 10; return Number.isInteger(r) ? String(r) : r.toFixed(1).replace('.', ','); };

function People({ groups, error, onChanged }) {
  if (error) {
    return <div style={{ marginTop: 18, fontSize: 12.5, color: MUTED }}>{error}</div>;
  }
  if (!groups || groups.length === 0) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ ...st.eyebrow, marginBottom: 8 }}>
        <i className="fa-solid fa-user-group" style={{ color: AMBER, marginRight: 6 }} />People for the work
      </div>
      {groups.map(g => <PeopleGroup key={g.group_id} g={g} onChanged={onChanged} />)}
    </div>
  );
}

function PeopleGroup({ g, onChanged }) {
  const open = (g.check || []).filter(x => !x.covered);
  const covered = (g.check || []).filter(x => x.covered);
  const slots = [1, 2, 3].map(slot => ({ slot, list: open.filter(x => x.slot === slot) })).filter(x => x.list.length);
  const title = `${g.shifts} shifts · ${(g.machines || []).join(', ') || 'these machines'} · ${fmtShort(g.start_date)} – ${fmtShort(g.end_date)}`;
  return (
    <div style={{ padding: '12px 14px', background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 10, marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 6 }}>{title}</div>
      {open.length === 0 && covered.length === 0 && (
        <div style={{ fontSize: 12.5, color: GREEN }}>
          <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />Every shift has people for the work this re-flow puts there.
        </div>
      )}
      {slots.map(({ slot, list }) => <ShortShift key={slot} groupId={g.group_id} slot={slot} list={list} onChanged={onChanged} />)}
      {covered.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: DIM, lineHeight: 1.55 }}>
          <i className="fa-solid fa-shield-halved" style={{ color: TEAL, marginRight: 6 }} />
          Covered: {covered.map(x => `${dayName(x.date)} ${SHIFT[x.slot]}`).join(', ')}
          {covered[covered.length - 1].covered.says ? <> — {covered[covered.length - 1].covered.says}</> : null}
          {covered[covered.length - 1].covered.by ? <span style={{ color: MUTED }}> ({covered[covered.length - 1].covered.by})</span> : null}
        </div>
      )}
    </div>
  );
}

function ShortShift({ groupId, slot, list, onChanged }) {
  const [mode, setMode] = useState(null);     // null | 'add' | 'cover'
  const [who, setWho] = useState(1);
  const [hours, setHours] = useState(8);
  const [how, setHow] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const worst = list.reduce((w, x) => (x.verdict === 'short' ? 'short' : w), list[0].verdict);
  const v = VERDICT[worst] || VERDICT.tight;
  const input = { width: 56, padding: '6px 8px', background: '#141920', border: `1px solid ${BORDER}`, borderRadius: 7, color: TEXT, fontSize: 13, textAlign: 'center' };
  const small = { padding: '6px 11px', fontSize: 12, borderRadius: 7, cursor: 'pointer', border: `1px solid ${BORDER}`, background: 'transparent', color: TEXT };

  async function addPerson() {
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/v1/shift-windows/${groupId}/staff`, { dates: list.map(x => x.date), slot, people: Number(who), hours: Number(hours) });
      setMode(null);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not add the person');
    } finally { setBusy(false); }
  }
  async function cover() {
    setBusy(true); setErr(null);
    try {
      await api.put(`/api/v1/shift-windows/${groupId}/cover`, { how, note: note.trim() || null, shifts: list.map(x => ({ date: x.date, slot })) });
      setMode(null);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not save how it is covered');
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
      <div style={{ fontSize: 12.5, color: TEXT, fontWeight: 600, textTransform: 'capitalize' }}>
        {SHIFT[slot]} <span style={{ color: v.color, textTransform: 'none', fontWeight: 700 }}>· {v.word}</span>
        <span style={{ color: MUTED, fontWeight: 400, textTransform: 'none' }}> on {list.length} day{list.length === 1 ? '' : 's'}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
        {list.map(x => (
          <div key={x.date} title={x.says} style={{ fontSize: 12, color: DIM, display: 'flex', gap: 8 }}>
            <span style={{ width: 92, color: TEXT }}>{dayName(x.date)}</span>
            <span>needs about {h(x.need)} h, the people give {h(x.have)} h</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4, lineHeight: 1.5 }}>{list[0].says}</div>

      {!mode && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" style={small} onClick={() => setMode('add')}>
            <i className="fa-solid fa-user-plus" style={{ marginRight: 6, color: TEAL }} />Add a person for these days
          </button>
          <button type="button" style={small} onClick={() => setMode('cover')}>
            <i className="fa-solid fa-shield-halved" style={{ marginRight: 6, color: TEAL }} />I've got it covered
          </button>
        </div>
      )}

      {mode === 'add' && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: DIM }}>
          <span>Add</span>
          <input type="number" min={0.5} step={0.5} value={who} onChange={e => setWho(e.target.value)} style={input} aria-label="People to add" />
          <span>{Number(who) === 1 ? 'person' : 'people'} for</span>
          <input type="number" min={1} max={12} step={0.5} value={hours} onChange={e => setHours(e.target.value)} style={input} aria-label="Hours each" />
          <span>h to the {SHIFT[slot]} on {list.length === 1 ? dayName(list[0].date) : `these ${list.length} days`}, with this window.</span>
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            <button type="button" style={small} onClick={() => setMode(null)}>Cancel</button>
            <button type="button" style={{ ...small, background: TEAL, color: '#04201d', border: 'none', fontWeight: 700 }} disabled={busy} onClick={addPerson}>
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {mode === 'cover' && (
        <div style={{ marginTop: 8, padding: '10px 12px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: 9 }}>
          <div style={{ fontSize: 12.5, color: TEXT, marginBottom: 6 }}>How is it covered? Kept with the window, so you can see why later.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {HOW.map(([k, label]) => (
              <label key={k} style={{ fontSize: 12.5, color: DIM, display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={how.includes(k)}
                  onChange={e => setHow(list0 => (e.target.checked ? [...list0, k] : list0.filter(x => x !== k)))} />
                {label}
              </label>
            ))}
          </div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={500}
            placeholder="In your own words, e.g. I stay until 18:00; Ana does the boxes"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '8px 10px', background: '#141920', border: `1px solid ${BORDER}`, borderRadius: 7, color: TEXT, fontSize: 12.5, fontFamily: 'system-ui', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" style={small} onClick={() => setMode(null)}>Cancel</button>
            <button type="button" disabled={busy || (!how.length && !note.trim())} onClick={cover}
              style={{ ...small, background: TEAL, color: '#04201d', border: 'none', fontWeight: 700, opacity: (!how.length && !note.trim()) ? 0.45 : 1 }}>
              {busy ? 'Saving…' : `Covered for ${list.length === 1 ? 'this shift' : `these ${list.length} shifts`}`}
            </button>
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ── a mould on two machines the same day (Shift Windows §7) ────────────────
//
// One at a time: settle it, the preview recalculates, the next (if any)
// appears. Every way out says what it does before it is chosen; one that would
// make a delivery late, or touch the frozen zone or a running job, is shown
// greyed with the reason rather than left out.
function Clash({ c, more, onChoose }) {
  return (
    <div style={{ marginTop: 16, padding: '13px 15px', background: `${RED}0d`, border: `1px solid ${RED}55`, borderRadius: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fca5a5', display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="fa-solid fa-triangle-exclamation" />
        A mould on two machines
        {more > 0 && <span style={{ fontWeight: 500, color: MUTED, fontSize: 12 }}>· {more} more after this</span>}
      </div>
      <div style={{ fontSize: 12.5, color: TEXT, lineHeight: 1.55, marginTop: 6 }}>{c.says}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 10 }}>
        {(c.options || []).map(o => (
          <div key={o.do} role="button" tabIndex={o.ok ? 0 : -1}
            onClick={() => o.ok && onChoose(o)}
            onKeyDown={e => { if (o.ok && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onChoose(o); } }}
            style={{ padding: '10px 12px', background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 8,
                     cursor: o.ok ? 'pointer' : 'default', opacity: o.ok ? 1 : 0.55 }}
            onMouseEnter={e => { if (o.ok) e.currentTarget.style.borderColor = TEAL; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{o.says}</div>
            <div style={{ fontSize: 12, color: DIM, lineHeight: 1.5, marginTop: 3 }}>{o.ok ? o.detail : o.why}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: MUTED, marginTop: 8 }}>Or cancel the re-flow below: nothing is written until Apply.</div>
    </div>
  );
}

// What has been chosen so far, and what the preview just recalculated after.
function Choices({ constraints, lastChoice, onStartAgain, pins = [], pinsError = null, onRelease, onChoose }) {
  const held = constraints.holds || [];
  const kept = constraints.accepted || [];
  const pinOf = id => pins.find(p => p.plan_id === id) || null;
  const stepped = constraints.step_over || [];
  return (
    <div style={{ marginTop: 14, padding: '10px 12px', background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 9, fontSize: 12, color: DIM, lineHeight: 1.55 }}>
      {lastChoice && <div style={{ color: TEXT }}><i className="fa-solid fa-rotate" style={{ color: TEAL, marginRight: 6 }} />Recalculated after {lastChoice.startsWith('you ') ? lastChoice : <>you chose: <b>{lastChoice}</b></>}.</div>}
      {held.map(h => {
        const p = h.why === 'pin' ? pinOf(h.plan_id) : null;
        const over = stepped.find(x => x.plan_id === h.plan_id);
        return (
          <div key={h.plan_id} style={{ marginTop: 4 }}>
            <i className="fa-solid fa-thumbtack" style={{ color: AMBER, marginRight: 6, fontSize: 10 }} />
            {h.says || 'A run is held'}{h.until ? ` — not before ${fmtShort(h.until)}` : ' — where it is'}
            {over && <span style={{ color: TEAL }}> · the next {over.ahead.length === 1 ? 'run goes' : `${over.ahead.length} runs go`} ahead of it</span>}
            {onRelease && (
              <button onClick={() => onRelease(h.plan_id, (p && p.part) || null)}
                style={{ marginLeft: 8, background: 'none', border: 'none', color: TEAL, cursor: 'pointer', padding: 0, fontSize: 12 }}>Release</button>
            )}
            {p && <div style={{ color: '#fcd34d', marginLeft: 16 }}>{p.says}</div>}
            {p && (p.step_over || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '6px 0 4px 16px' }}>
                {p.step_over.map((o, i) => (
                  <div key={i} role="button" tabIndex={o.ok ? 0 : -1}
                    onClick={() => o.ok && onChoose && onChoose(o)}
                    onKeyDown={e => { if (o.ok && onChoose && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onChoose(o); } }}
                    style={{ padding: '8px 10px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, cursor: o.ok ? 'pointer' : 'default', opacity: o.ok ? 1 : 0.55 }}
                    onMouseEnter={e => { if (o.ok) e.currentTarget.style.borderColor = TEAL; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: TEXT }}>{o.says}</div>
                    <div style={{ fontSize: 12, color: DIM, marginTop: 2 }}>{o.ok ? o.detail : o.why}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {pinsError && <div style={{ color: MUTED, marginTop: 4 }}>{pinsError}</div>}
      {kept.length > 0 && (
        <div><i className="fa-solid fa-hand" style={{ color: AMBER, marginRight: 6, fontSize: 10 }} />{kept.length} mould clash{kept.length === 1 ? '' : 'es'} kept, to move one yourself</div>
      )}
      {(held.length > 0 || kept.length > 0) && (
        <button onClick={onStartAgain} style={{ marginTop: 6, background: 'none', border: 'none', color: TEAL, cursor: 'pointer', padding: 0, fontSize: 12 }}>
          Start the choices again
        </button>
      )}
    </div>
  );
}
